import { useMemo, useCallback } from 'react';

import { AppState, useSelector } from '@tmapy/redux';
import { cobraLogin } from '@tmapy/auth';

import type { ACLResource, ACLAction } from '../lib/acl';
import {
  createActionFilter,
  resolveAccessPolicyStatementConditions,
  ACCESS_POLICY_EFFECT,
} from '../lib/acl';
import { evaluateCondition } from '../auth/policies';
import { createLoginRedirectUrl } from '../utils/url';

import { useAppContext } from './useAppContext';
import { useHistoryAction } from './useHistoryAction';

export type UseAuth = AppState['auth'] & {
  hasPermission(resource: ACLResource, action: ACLAction, context: object | null): boolean;
  requireLoggedIn(): boolean;
};

/**
 * Read auth state from redux, provides `isInRole`, `requireLoggedIn`, `requireRole` helpers
 */
export const useAuth = (): UseAuth => {
  const auth = useSelector((state) => state.auth);
  const app = useAppContext();
  const error = useHistoryAction('error', app);
  const { appType } = app;
  const userNuts = auth.orp_id;
  const { loginUrl, auth: authConfig } = useSelector((state) => state.app.config);

  /**
   * Ask Access Control engine if user can perform `action` on `resource`
   * @param resource Resource name
   * @param action Action name
   * @param context Context for conditions, for example resource data
   */
  const hasPermission = useCallback(
    (resource: ACLResource, action: ACLAction, context: object | null): boolean => {
      const statements = auth.policies[resource]?.filter(createActionFilter(action));
      return (
        resolveAccessPolicyStatementConditions(statements, (condition) =>
          evaluateCondition(condition, { appType, userNuts, ...context }),
        ) === ACCESS_POLICY_EFFECT.ALLOW
      );
    },
    [auth.policies, appType, userNuts],
  );

  /**
   * Check if user is logged in.
   * If user is not logged in, it will shedule redirect to login page.
   *
   * @example
   * ```
   * const auth = useAuth();
   * if (!auth.requireLoggedIn()) return null;
   * ```
   */
  const requireLoggedIn = useCallback((): boolean => {
    if (!auth.isAuthenticated) {
      if (auth.error) {
        console.info('User authentication error, redirecting to error page');
        window.setImmediate(error);
        return false;
      }
      console.info('User is not authenticated, redirecting to login');
      if (authConfig) {
        window.setImmediate(() => cobraLogin());
      } else if (loginUrl) {
        window.setImmediate(() => (window.location.href = createLoginRedirectUrl(loginUrl)));
      }
      return false;
    }
    return true;
  }, [auth.error, auth.isAuthenticated, error, authConfig, loginUrl]);

  return useMemo(
    () => ({
      ...auth,
      hasPermission,
      requireLoggedIn,
    }),
    [auth, hasPermission, requireLoggedIn],
  );
};
